import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { resolveFrontendApiBase } from '../utils/apiBase';
import { Bar, BarChart, CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { Alert, Button, Card, Space, Table, Tag, Typography, message, theme } from 'antd'
import adminAxios from './adminAxios'
import { exportUsageCountersToExcel } from './exportUsageCounters'
import './AdminDashboardSurfaces.css'

const API_BASE = resolveFrontendApiBase()

const { Title, Text } = Typography

type TimeRange = '7d' | '30d' | '90d'

type CostAvailability = {
  providerCostUsdKnown?: boolean
  chargedCreditsKnown?: boolean
  estimatedTokenCostUsdKnown?: boolean
}

type TokenTotals = {
  inputTokens?: number
  cachedTokens?: number
  outputTokens?: number
  reasoningTokens?: number
  totalTokens?: number
}

type BreakdownRow = {
  key: string
  label: string
  eventCount?: number
  providerCostUsd?: number | null
  chargedCredits?: number | null
  estimatedTokenCostUsd?: number | null
  tokenTotals?: TokenTotals
  uniqueBooks?: number
  uniqueChapters?: number
  uniqueRuns?: number
  avgProviderCostUsdPerChapter?: number | null
  avgEstimatedTokenCostUsdPerChapter?: number | null
  avgProviderCostUsdPerRun?: number | null
  avgEstimatedTokenCostUsdPerRun?: number | null
  availability?: CostAvailability
}

type OverviewResponse = {
  dateRange: { start: string; end: string }
  lifetime: {
    translatedWords?: number
    proofreadWords?: number
    audioSeconds?: number
    coverGenerationCount?: number
    coverUploadCount?: number
    bookUploads?: number
    userSignups?: number
    providerCostUsd?: number
    chargedCredits?: number
    estimatedTokenCostUsd?: number
  }
  window: {
    translatedWords?: number
    translatedChapters?: number
    proofreadWords?: number
    audioSeconds?: number
    audioItemCount?: number
    fullAudiobookCount?: number
    coverGenerationCount?: number
    coverUploadCount?: number
    bookUploads?: number
    userSignups?: number
    providerCostUsd?: number
    chargedCredits?: number
    estimatedTokenCostUsd?: number
  }
  processSummaries?: BreakdownRow[]
  topBreakdowns?: {
    provider?: BreakdownRow[]
    model?: BreakdownRow[]
    languagePair?: BreakdownRow[]
  }
}

type TrendRow = {
  date: string
  translatedWords?: number
  proofreadWords?: number
  audioSeconds?: number
  coverGenerationCount?: number
  coverUploadCount?: number
  providerCostUsd?: number
  chargedCredits?: number
  estimatedTokenCostUsd?: number
  translationProviderCostUsd?: number
  translationEstimatedTokenCostUsd?: number
  proofreadingProviderCostUsd?: number
  proofreadingEstimatedTokenCostUsd?: number
  audioProviderCostUsd?: number
  coverProviderCostUsd?: number
  availability?: {
    translationProviderCostUsdKnown?: boolean
    proofreadingProviderCostUsdKnown?: boolean
    audioProviderCostUsdKnown?: boolean
    coverProviderCostUsdKnown?: boolean
  }
}

type TrendsResponse = {
  dateRange: { start: string; end: string }
  items: TrendRow[]
}

type CostBreakdownResponse = {
  process: string
  summary: BreakdownRow
  items: BreakdownRow[]
  trends: Array<{
    date: string
    providerCostUsd?: number | null
    chargedCredits?: number | null
    estimatedTokenCostUsd?: number | null
  }>
}

type ObserveCostProjection = {
  projectionOnly?: boolean
  comparisonBasis?: string
  additionalModelCallsMade?: number
  actualComparableCostUsd?: number
  projectedRoutedCostUsd?: number
  projectedTargetedReviewCostUsd?: number
  projectedTotalCostUsd?: number
  projectedSavingsUsd?: number
  projectedSavingsPercent?: number | null
  projectionEventCount?: number
  pricedEventCount?: number
  missingPricingEventCount?: number
  pricingCoverageRate?: number
  pricingComplete?: boolean
  reviewRequiredCount?: number
  reviewClearCount?: number
  wouldRunCount?: number
  wouldSkipCount?: number
  economyProjectionCount?: number
  escalationProjectionCount?: number
  qualityActualComparableCostUsd?: number
  qualityCostCoverageRatio?: number
  projectedModels?: Record<string, { eventCount?: number; projectedCostUsd?: number }>
}

type TranslationQualityCostRow = {
  stage: string
  eventCount?: number
  costTotalUsd?: number
  noChangeRate?: number
  observeProjection?: ObserveCostProjection
}

type TranslationQualityCostResponse = {
  dateRange?: { start?: string; end?: string }
  coverage?: {
    daysRequested?: number
    daysMaterialized?: number
    translatedChapters?: number
    translatedWords?: number
  }
  reconciliation?: {
    withinOnePercent?: boolean
    differenceUsd?: number
  }
  summary?: {
    primaryCostUsd?: number
    qualityCostUsd?: number
    totalTranslationStageCostUsd?: number
    qualityShare?: number
    observeProjection?: ObserveCostProjection
  }
  items?: TranslationQualityCostRow[]
  trends?: Array<{
    date: string
    qualityCostUsd?: number
    primaryCostUsd?: number
    observeProjection?: ObserveCostProjection
  }>
}

type JobsResponse = {
  items: AnalyticsAdminJob[]
}

type AnalyticsAdminJob = {
  job_id: string
  job_type: string
  status: string
  trigger_source?: string
  created_by?: string | null
  created_at?: string
  started_at?: string
  finished_at?: string
  request?: Record<string, any>
  counts?: Record<string, any>
  errors_preview?: string[]
  result?: any
  schedule_key?: string | null
}

type AnalyticsScheduleStatus = {
  scheduleKey: string
  label: string
  cadenceLabel: string
  nextRunAt?: string | null
  lastRunAt?: string | null
  lastStatus?: string | null
  lastJobId?: string | null
  lastResult?: any
}

type SchedulesResponse = {
  items: AnalyticsScheduleStatus[]
}

type YearlyResponse = {
  year: number
  totals: {
    translatedWords?: number
    translatedChapters?: number
    translatedBooks?: number
    proofreadWords?: number
    audioSeconds?: number
    audioItemCount?: number
    fullAudiobookCount?: number
    coverGenerationCount?: number
    coverUploadCount?: number
    booksAddedThisYear?: number
    userCount?: number
  }
  topLanguages?: Array<{ language: string; value: number }>
  topModels?: Array<{ model: string; value: number }>
  topProviders?: Array<{ provider: string; value: number }>
  topUsers?: Array<Record<string, any>>
}

type LegacyUsageAggregates = {
  totals?: { input?: number; output?: number; total?: number; cached?: number; reasoning?: number; output_non_reasoning?: number }
  by_day?: Array<{ day: string; total?: number }>
}

type LegacyModelBreakdown = { models?: Array<Record<string, any>> }

type AnalyticsTabProps = {
  isAdmin: boolean
}

const PROCESS_LABELS: Record<string, string> = {
  translation: 'Translation',
  proofreading: 'Proofreading',
  audio_generation: 'Audio',
  cover_generation: 'Cover',
  book_upload: 'Uploads',
  user_signup: 'Signups',
}

const DEFAULT_TIME_RANGE: TimeRange = '30d'

const formatNumber = (value?: number | null): string => {
  const normalized = Number(value ?? 0)
  return normalized.toLocaleString()
}

const formatCompactNumber = (value?: number | null): string => {
  const normalized = Number(value ?? 0)
  if (Math.abs(normalized) >= 1_000_000) return `${(normalized / 1_000_000).toFixed(1)}M`
  if (Math.abs(normalized) >= 1_000) return `${(normalized / 1_000).toFixed(1)}k`
  return normalized.toLocaleString()
}

const formatCurrency = (value?: number | null): string => {
  if (value === null || value === undefined) return '—'
  const normalized = Number(value)
  if (Math.abs(normalized) >= 1_000_000) return `$${(normalized / 1_000_000).toFixed(1)}M`
  if (Math.abs(normalized) >= 1_000) return `$${(normalized / 1_000).toFixed(1)}k`
  if (Math.abs(normalized) < 1) return `$${normalized.toFixed(4)}`
  return `$${normalized.toFixed(2)}`
}

const formatCredits = (value?: number | null): string => {
  if (value === null || value === undefined) return '—'
  return formatCompactNumber(value)
}

const formatPercent = (value?: number | null): string => {
  if (value === null || value === undefined) return '—'
  return `${(Number(value) * 100).toFixed(1)}%`
}

const formatDurationHours = (seconds?: number | null): string => {
  const normalized = Number(seconds ?? 0)
  return `${(normalized / 3600).toFixed(1)}h`
}

const formatIsoShort = (value?: string | null): string => {
  if (!value) return '—'
  try {
    return new Date(value).toLocaleString()
  } catch {
    return value
  }
}

const emptyBreakdowns = (): Record<string, BreakdownRow[]> => ({
  process: [],
  provider: [],
  model: [],
  language_pair: [],
})

const emptyCostBreakdowns = (): Record<string, CostBreakdownResponse | null> => ({
  translation: null,
  proofreading: null,
  audio_generation: null,
  cover_generation: null,
})

const todayIso = (): string => new Date().toISOString().slice(0, 10)

const computeDateWindow = (timeRange: TimeRange): { start: string; end: string } => {
  const end = new Date()
  const start = new Date(end)
  if (timeRange === '7d') start.setUTCDate(start.getUTCDate() - 6)
  if (timeRange === '30d') start.setUTCDate(start.getUTCDate() - 29)
  if (timeRange === '90d') start.setUTCDate(start.getUTCDate() - 89)
  return {
    start: start.toISOString().slice(0, 10),
    end: end.toISOString().slice(0, 10),
  }
}

const isJobActive = (job?: AnalyticsAdminJob | null): boolean =>
  ['queued', 'running'].includes(String(job?.status || '').toLowerCase())

const sectionGridStyle: React.CSSProperties = {
  display: 'grid',
  gap: 16,
  gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
}

const inlineFieldGridStyle: React.CSSProperties = {
  display: 'grid',
  gap: 12,
  gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
}

const filterCardStyle: React.CSSProperties = {
  display: 'grid',
  gap: 16,
}

const tableScrollX = { x: 900 }

const AnalyticsTab: React.FC<AnalyticsTabProps> = ({ isAdmin }) => {
  const { token } = theme.useToken()
  const [timeRange, setTimeRange] = useState<TimeRange>(DEFAULT_TIME_RANGE)
  const [startDate, setStartDate] = useState<string>(() => computeDateWindow(DEFAULT_TIME_RANGE).start)
  const [endDate, setEndDate] = useState<string>(() => computeDateWindow(DEFAULT_TIME_RANGE).end)
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getUTCFullYear())

  const [loading, setLoading] = useState<boolean>(false)
  const [trendsLoading, setTrendsLoading] = useState<boolean>(false)
  const [breakdownsLoading, setBreakdownsLoading] = useState<boolean>(false)
  const [costLoading, setCostLoading] = useState<boolean>(false)
  const [qualityCostLoading, setQualityCostLoading] = useState<boolean>(false)
  const [opsLoading, setOpsLoading] = useState<boolean>(false)
  const [yearlyLoading, setYearlyLoading] = useState<boolean>(false)
  const [diagnosticsLoading, setDiagnosticsLoading] = useState<boolean>(false)
  const [overview, setOverview] = useState<OverviewResponse | null>(null)
  const [trends, setTrends] = useState<TrendsResponse | null>(null)
  const [breakdowns, setBreakdowns] = useState<Record<string, BreakdownRow[]>>(emptyBreakdowns())
  const [costBreakdowns, setCostBreakdowns] = useState<Record<string, CostBreakdownResponse | null>>(emptyCostBreakdowns())
  const [translationQualityCosts, setTranslationQualityCosts] = useState<TranslationQualityCostResponse | null>(null)
  const [yearly, setYearly] = useState<YearlyResponse | null>(null)
  const [jobs, setJobs] = useState<AnalyticsAdminJob[]>([])
  const [schedules, setSchedules] = useState<AnalyticsScheduleStatus[]>([])
  const [loadNotice, setLoadNotice] = useState<string | null>(null)

  const [legacyUsage, setLegacyUsage] = useState<LegacyUsageAggregates | null>(null)
  const [legacyModelBreakdown, setLegacyModelBreakdown] = useState<LegacyModelBreakdown | null>(null)
  const [legacyCounters, setLegacyCounters] = useState<Record<string, any>>({})

  const [selectedCostProcess, setSelectedCostProcess] = useState<string>('translation')
  const [legacyRebuildLoading, setLegacyRebuildLoading] = useState<boolean>(false)
  const [opsSubmitting, setOpsSubmitting] = useState<boolean>(false)
  const [opsForm, setOpsForm] = useState({
    dry_run: true,
    year: String(new Date().getUTCFullYear()),
    start_date: '',
    end_date: '',
    user_id: '',
    book_id: '',
    allow_global: false,
    limit: '',
    include_stage: true,
    include_generation: true,
    include_business: true,
    rebuild_rollups: true,
  })

  const canonicalParams = useMemo(
    () => ({
      start: startDate,
      end: endDate,
    }),
    [startDate, endDate]
  )

  const currentCostBreakdown = costBreakdowns[selectedCostProcess] || null

  const extractLoadMessage = useCallback((error: any, fallback: string) => (
    error?.response?.data?.detail || error?.message || fallback
  ), [])

  const recordLoadNotice = useCallback((error: any, fallback: string) => {
    const detail = extractLoadMessage(error, fallback)
    setLoadNotice(detail)
    message.warning(detail)
  }, [extractLoadMessage])

  const processCardStyle = useMemo<React.CSSProperties>(
    () => ({
      border: `1px solid ${token.colorBorderSecondary}`,
      borderRadius: 12,
      padding: 16,
      background: token.colorBgContainer,
      color: token.colorText,
      boxShadow: 'none',
    }),
    [token]
  )

  const chartAxisStyle = useMemo(
    () => ({ stroke: token.colorTextTertiary, fontSize: 12, fill: token.colorTextTertiary }),
    [token]
  )

  const chartGridStroke = token.colorBorderSecondary
  const chartTooltipContentStyle = useMemo(
    () => ({
      backgroundColor: token.colorBgElevated,
      border: `1px solid ${token.colorBorderSecondary}`,
      borderRadius: 10,
      color: token.colorText,
    }),
    [token]
  )

  const chartTooltipLabelStyle = useMemo(() => ({ color: token.colorTextHeading }), [token])

  const chartLegendWrapperStyle = useMemo(
    () => ({ color: token.colorTextSecondary, paddingTop: 8 }),
    [token]
  )

  const chartPalette = useMemo(
    () => ({
      primary: token.colorPrimary,
      success: token.colorSuccess,
      danger: token.colorError,
      warning: token.colorWarning,
      info: token.colorInfo,
    }),
    [token]
  )

  const setRangeFromPreset = useCallback((nextRange: TimeRange) => {
    const nextWindow = computeDateWindow(nextRange)
    setTimeRange(nextRange)
    setStartDate(nextWindow.start)
    setEndDate(nextWindow.end)
  }, [])

  const loadBreakdownData = useCallback(async () => {
    if (!isAdmin) return
    setBreakdownsLoading(true)
    try {
      const { data } = await adminAxios.get(`${API_BASE}/admin/canonical-analytics/breakdowns-batch`, {
        params: { ...canonicalParams, dimensions: 'process,provider,model,language_pair' },
      })
      const items = data?.items || {}
      setBreakdowns({
        process: items.process || [],
        provider: items.provider || [],
        model: items.model || [],
        language_pair: items.language_pair || [],
      })
    } catch (error: any) {
      recordLoadNotice(error, 'Failed to load analytics breakdowns')
    } finally {
      setBreakdownsLoading(false)
    }
  }, [canonicalParams, isAdmin, recordLoadNotice])

  const loadCostBreakdown = useCallback(async (process: string) => {
    if (!isAdmin) return
    setCostLoading(true)
    try {
      const { data } = await adminAxios.get(`${API_BASE}/admin/canonical-analytics/cost-breakdown`, {
        params: { ...canonicalParams, process },
      })
      setCostBreakdowns((current) => ({ ...current, [process]: data }))
    } catch (error: any) {
      recordLoadNotice(error, `Failed to load ${process} costs`)
    } finally {
      setCostLoading(false)
    }
  }, [canonicalParams, isAdmin, recordLoadNotice])

  const loadTranslationQualityCosts = useCallback(async () => {
    if (!isAdmin) return
    setQualityCostLoading(true)
    try {
      const { data } = await adminAxios.get(
        `${API_BASE}/admin/canonical-analytics/translation-quality-costs`,
        { params: canonicalParams }
      )
      setTranslationQualityCosts(data)
    } catch (error: any) {
      recordLoadNotice(error, 'Failed to load translation quality cost comparison')
    } finally {
      setQualityCostLoading(false)
    }
  }, [canonicalParams, isAdmin, recordLoadNotice])

  const loadJobsData = useCallback(async (showSpinner: boolean = true) => {
    if (!isAdmin) return
    if (showSpinner) {
      setOpsLoading(true)
    }
    try {
      const jobsRes = await adminAxios.get(`${API_BASE}/admin/canonical-analytics/jobs`, { params: { limit: 25 } })
      setJobs((jobsRes.data as JobsResponse)?.items || [])
    } catch (error: any) {
      recordLoadNotice(error, 'Failed to load analytics jobs')
    } finally {
      if (showSpinner) {
        setOpsLoading(false)
      }
    }
  }, [isAdmin, recordLoadNotice])

  const loadOpsData = useCallback(async () => {
    if (!isAdmin) return
    setOpsLoading(true)
    try {
      const [jobsRes, schedulesRes] = await Promise.all([
        adminAxios.get(`${API_BASE}/admin/canonical-analytics/jobs`, { params: { limit: 25 } }),
        adminAxios.get(`${API_BASE}/admin/canonical-analytics/schedules`),
      ])
      setJobs((jobsRes.data as JobsResponse)?.items || [])
      setSchedules((schedulesRes.data as SchedulesResponse)?.items || [])
    } catch (error: any) {
      recordLoadNotice(error, 'Failed to load analytics operations')
    } finally {
      setOpsLoading(false)
    }
  }, [isAdmin, recordLoadNotice])

  const loadYearlyData = useCallback(async () => {
    if (!isAdmin) return
    setYearlyLoading(true)
    try {
      const yearlyRes = await adminAxios.get(`${API_BASE}/admin/canonical-analytics/yearly`, { params: { year: selectedYear } })
      setYearly(yearlyRes.data)
    } catch (error: any) {
      recordLoadNotice(error, 'Failed to load yearly analytics')
    } finally {
      setYearlyLoading(false)
    }
  }, [isAdmin, recordLoadNotice, selectedYear])

  const loadTrendsData = useCallback(async () => {
    if (!isAdmin) return
    setTrendsLoading(true)
    try {
      const trendsRes = await adminAxios.get(`${API_BASE}/admin/canonical-analytics/trends`, { params: canonicalParams })
      setTrends(trendsRes.data)
    } catch (error: any) {
      recordLoadNotice(error, 'Failed to load analytics trends')
    } finally {
      setTrendsLoading(false)
    }
  }, [canonicalParams, isAdmin, recordLoadNotice])

  const loadCanonicalData = useCallback(async () => {
    if (!isAdmin) return
    setLoading(true)
    setLoadNotice(null)
    try {
      const overviewRes = await adminAxios.get(`${API_BASE}/admin/canonical-analytics/overview`, {
        params: { ...canonicalParams, include_breakdowns: false },
      })

      setOverview(overviewRes.data)
      setTrends(null)
      setBreakdowns(emptyBreakdowns())
      setCostBreakdowns(emptyCostBreakdowns())
      void loadTranslationQualityCosts()
      void loadOpsData()
      void loadYearlyData()
    } catch (error: any) {
      const detail = extractLoadMessage(error, 'Failed to load canonical analytics overview')
      setLoadNotice(detail)
      message.error(detail)
    } finally {
      setLoading(false)
    }
  }, [
    canonicalParams,
    isAdmin,
    extractLoadMessage,
    loadTranslationQualityCosts,
    loadOpsData,
    loadYearlyData,
  ])

  const loadLegacyDiagnostics = useCallback(async () => {
    if (!isAdmin) return
    setDiagnosticsLoading(true)
    try {
      const [usageRes, modelRes, countersRes] = await Promise.all([
        adminAxios.get(`${API_BASE}/admin/usage-aggregates`, {
          params: {
            days: 30,
            include_batch: true,
            include_validation: true,
          },
        }),
        adminAxios.get(`${API_BASE}/admin/model-usage-breakdown`, {
          params: { days: 30 },
        }),
        adminAxios.get(`${API_BASE}/admin/stats-counters`),
      ])
      setLegacyUsage(usageRes.data)
      setLegacyModelBreakdown(modelRes.data)
      setLegacyCounters(countersRes.data || {})
    } catch (error: any) {
      recordLoadNotice(error, 'Failed to load legacy diagnostics')
    } finally {
      setDiagnosticsLoading(false)
    }
  }, [isAdmin, recordLoadNotice])

  useEffect(() => {
    if (!isAdmin) return
    void loadCanonicalData()
  }, [isAdmin, loadCanonicalData])

  useEffect(() => {
    if (!isAdmin) return
    const hasActiveJobs = jobs.some((job) => isJobActive(job))
    if (!hasActiveJobs) return
    const interval = window.setInterval(() => {
      void loadJobsData(false)
    }, 5000)
    return () => window.clearInterval(interval)
  }, [isAdmin, jobs, loadJobsData])

  const submitBackfill = useCallback(async () => {
    const scoped = Boolean(
      opsForm.year || opsForm.start_date || opsForm.end_date || opsForm.user_id || opsForm.book_id
    )
    if (!opsForm.allow_global && !scoped) {
      message.error('Provide a year, date window, user, or book, or explicitly allow a global run.')
      return
    }
    if (opsForm.allow_global && !opsForm.dry_run && !scoped) {
      const confirmed = window.confirm(
        'This will enqueue a non-dry-run global canonical analytics backfill. Continue?'
      )
      if (!confirmed) return
      const phrase = window.prompt('Type GLOBAL BACKFILL to confirm.')
      if ((phrase || '').trim() !== 'GLOBAL BACKFILL') {
        message.error('Global backfill confirmation phrase did not match.')
        return
      }
    }

    setOpsSubmitting(true)
    try {
      const payload = {
        dry_run: opsForm.dry_run,
        year: opsForm.year ? Number(opsForm.year) : null,
        start_date: opsForm.start_date || null,
        end_date: opsForm.end_date || null,
        user_id: opsForm.user_id || null,
        book_id: opsForm.book_id || null,
        allow_global: opsForm.allow_global,
        limit: opsForm.limit ? Number(opsForm.limit) : null,
        include_stage: opsForm.include_stage,
        include_generation: opsForm.include_generation,
        include_business: opsForm.include_business,
        rebuild_rollups: opsForm.rebuild_rollups,
      }
      const res = await adminAxios.post(`${API_BASE}/admin/canonical-analytics/jobs/backfill`, payload)
      message.success(`Canonical backfill queued: ${res.data?.job_id || 'job created'}`)
      await loadJobsData(false)
    } catch (error: any) {
      message.error(error?.response?.data?.detail || error?.message || 'Failed to queue canonical backfill')
    } finally {
      setOpsSubmitting(false)
    }
  }, [loadJobsData, opsForm])

  const submitRollupRebuild = useCallback(async () => {
    setOpsSubmitting(true)
    try {
      const payload = {
        dry_run: opsForm.dry_run,
        year: opsForm.year ? Number(opsForm.year) : null,
      }
      const res = await adminAxios.post(
        `${API_BASE}/admin/canonical-analytics/jobs/rebuild-rollups`,
        payload
      )
      message.success(`Canonical rollup rebuild queued: ${res.data?.job_id || 'job created'}`)
      await loadJobsData(false)
    } catch (error: any) {
      message.error(error?.response?.data?.detail || error?.message || 'Failed to queue rollup rebuild')
    } finally {
      setOpsSubmitting(false)
    }
  }, [loadJobsData, opsForm.dry_run, opsForm.year])

  const rebuildLegacyUsageAggregates = useCallback(async () => {
    setLegacyRebuildLoading(true)
    try {
      await adminAxios.post(`${API_BASE}/admin/usage-aggregates/rebuild`, { days: 30 })
      message.success('Legacy usage rebuild queued')
      await loadLegacyDiagnostics()
    } catch (error: any) {
      message.error(error?.response?.data?.detail || error?.message || 'Failed to queue legacy usage rebuild')
    } finally {
      setLegacyRebuildLoading(false)
    }
  }, [loadLegacyDiagnostics])

  const windowProcessRows = overview?.processSummaries?.length ? overview.processSummaries : breakdowns.process
  const processSummaryByKey = useMemo(() => {
    const byKey: Record<string, BreakdownRow> = {}
    for (const row of breakdowns.process || []) {
      if (row?.key) byKey[row.key] = row
    }
    return byKey
  }, [breakdowns.process])
  const translationSummary = costBreakdowns.translation?.summary || processSummaryByKey.translation
  const proofreadingSummary = costBreakdowns.proofreading?.summary || processSummaryByKey.proofreading
  const audioSummary = costBreakdowns.audio_generation?.summary || processSummaryByKey.audio_generation
  const coverSummary = costBreakdowns.cover_generation?.summary || processSummaryByKey.cover_generation

  const processCostCards = [
    { key: 'translation', label: 'Translation', summary: translationSummary },
    { key: 'proofreading', label: 'Proofreading', summary: proofreadingSummary },
    { key: 'audio_generation', label: 'Audio', summary: audioSummary },
    { key: 'cover_generation', label: 'Cover', summary: coverSummary },
  ]

  const costTrendData = currentCostBreakdown?.trends || []
  const stageDrilldownRows = currentCostBreakdown?.items || []
  const qualityObserveSummary = translationQualityCosts?.summary?.observeProjection
  const qualityObserveAvailable = Number(qualityObserveSummary?.projectionEventCount || 0) > 0
  const qualityObserveTrendData = (translationQualityCosts?.trends || []).map((row) => ({
    date: row.date,
    actualComparableCostUsd: row.observeProjection?.actualComparableCostUsd,
    projectedTotalCostUsd: row.observeProjection?.projectedTotalCostUsd,
    projectedRoutedCostUsd: row.observeProjection?.projectedRoutedCostUsd,
    projectedReviewCostUsd: row.observeProjection?.projectedTargetedReviewCostUsd,
  }))
  const qualityProjectedModels = Object.entries(qualityObserveSummary?.projectedModels || {})
    .map(([model, values]) => ({ model, ...values }))
    .sort((left, right) => Number(right.projectedCostUsd || 0) - Number(left.projectedCostUsd || 0))

  const breakdownColumns = [
    { title: 'Label', dataIndex: 'label', key: 'label' },
    { title: 'Events', dataIndex: 'eventCount', key: 'eventCount', render: (value: number) => formatNumber(value) },
    {
      title: 'Provider USD',
      dataIndex: 'providerCostUsd',
      key: 'providerCostUsd',
      render: (value: number | null | undefined) => formatCurrency(value),
    },
    {
      title: 'Credits',
      dataIndex: 'chargedCredits',
      key: 'chargedCredits',
      render: (value: number | null | undefined) => formatCredits(value),
    },
    {
      title: 'Estimated Token USD',
      dataIndex: 'estimatedTokenCostUsd',
      key: 'estimatedTokenCostUsd',
      render: (value: number | null | undefined) => formatCurrency(value),
    },
  ]

  const costColumns = [
    { title: 'Stage / Operation', dataIndex: 'label', key: 'label' },
    { title: 'Events', dataIndex: 'eventCount', key: 'eventCount', render: (value: number) => formatNumber(value) },
    {
      title: 'Tokens',
      key: 'tokens',
      render: (_: any, row: BreakdownRow) => formatCompactNumber(row?.tokenTotals?.totalTokens || 0),
    },
    {
      title: 'Provider USD',
      dataIndex: 'providerCostUsd',
      key: 'providerCostUsd',
      render: (value: number | null | undefined) => formatCurrency(value),
    },
    {
      title: 'Credits',
      dataIndex: 'chargedCredits',
      key: 'chargedCredits',
      render: (value: number | null | undefined) => formatCredits(value),
    },
    {
      title: 'Estimated Token USD',
      dataIndex: 'estimatedTokenCostUsd',
      key: 'estimatedTokenCostUsd',
      render: (value: number | null | undefined) => formatCurrency(value),
    },
    {
      title: 'Avg / Chapter',
      key: 'avgPerChapter',
      render: (_: any, row: BreakdownRow) =>
        row.avgProviderCostUsdPerChapter != null
          ? formatCurrency(row.avgProviderCostUsdPerChapter)
          : formatCurrency(row.avgEstimatedTokenCostUsdPerChapter),
    },
    {
      title: 'Avg / Run',
      key: 'avgPerRun',
      render: (_: any, row: BreakdownRow) =>
        row.avgProviderCostUsdPerRun != null
          ? formatCurrency(row.avgProviderCostUsdPerRun)
          : formatCurrency(row.avgEstimatedTokenCostUsdPerRun),
    },
  ]

  const qualityCostColumns = [
    { title: 'Stage', dataIndex: 'stage', key: 'stage' },
    {
      title: 'Events',
      dataIndex: 'eventCount',
      key: 'eventCount',
      render: (value: number) => formatNumber(value),
    },
    {
      title: 'Current Actual',
      dataIndex: 'costTotalUsd',
      key: 'costTotalUsd',
      render: (value: number | null | undefined) => formatCurrency(value),
    },
    {
      title: 'Comparable Actual',
      key: 'actualComparable',
      render: (_: any, row: TranslationQualityCostRow) =>
        formatCurrency(row.observeProjection?.actualComparableCostUsd),
    },
    {
      title: 'Projected Routed',
      key: 'projectedRouted',
      render: (_: any, row: TranslationQualityCostRow) =>
        formatCurrency(row.observeProjection?.projectedRoutedCostUsd),
    },
    {
      title: 'Projected Review',
      key: 'projectedReview',
      render: (_: any, row: TranslationQualityCostRow) =>
        formatCurrency(row.observeProjection?.projectedTargetedReviewCostUsd),
    },
    {
      title: 'Projected Total',
      key: 'projectedTotal',
      render: (_: any, row: TranslationQualityCostRow) =>
        formatCurrency(row.observeProjection?.projectedTotalCostUsd),
    },
    {
      title: 'Projected Savings',
      key: 'projectedSavings',
      render: (_: any, row: TranslationQualityCostRow) =>
        formatCurrency(row.observeProjection?.projectedSavingsUsd),
    },
    {
      title: 'Review Flags',
      key: 'reviewFlags',
      render: (_: any, row: TranslationQualityCostRow) =>
        formatNumber(row.observeProjection?.reviewRequiredCount),
    },
    {
      title: 'Pricing Coverage',
      key: 'pricingCoverage',
      render: (_: any, row: TranslationQualityCostRow) =>
        formatPercent(row.observeProjection?.pricingCoverageRate),
    },
  ]

  const jobsColumns = [
    { title: 'Job', dataIndex: 'job_type', key: 'job_type' },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (value: string) => <Tag>{value}</Tag>,
    },
    {
      title: 'Trigger',
      key: 'trigger_source',
      render: (_: any, row: AnalyticsAdminJob) => row.trigger_source || 'manual_ui',
    },
    {
      title: 'Scope',
      key: 'scope',
      render: (_: any, row: AnalyticsAdminJob) => {
        const request = row.request || {}
        return [
          request.year ? `year=${request.year}` : null,
          request.start_date ? `start=${request.start_date}` : null,
          request.end_date ? `end=${request.end_date}` : null,
          request.user_id ? `user=${request.user_id}` : null,
          request.book_id ? `book=${request.book_id}` : null,
          request.allow_global ? 'global' : null,
        ]
          .filter(Boolean)
          .join(', ') || 'custom'
      },
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (value: string) => formatIsoShort(value),
    },
    {
      title: 'Finished',
      dataIndex: 'finished_at',
      key: 'finished_at',
      render: (value: string) => formatIsoShort(value),
    },
  ]

  if (!isAdmin) {
    return (
      <Card className="admin-dashboard-surface-card">
        <Title level={3}>Analytics</Title>
        <Text>Admin access is required.</Text>
      </Card>
    )
  }

  return (
    <div className="admin-dashboard-surface">
      <Card title="Filters" className="admin-dashboard-surface-card admin-analytics-toolbar-card">
        <div className="admin-analytics-toolbar" style={filterCardStyle}>
          <div style={inlineFieldGridStyle}>
            <label>
              <div>Preset</div>
              <select value={timeRange} onChange={(event) => setRangeFromPreset(event.target.value as TimeRange)}>
                <option value="7d">Last 7 days</option>
                <option value="30d">Last 30 days</option>
                <option value="90d">Last 90 days</option>
              </select>
            </label>
            <label>
              <div>Start</div>
              <input type="date" value={startDate} onChange={(event) => setStartDate(event.target.value || todayIso())} />
            </label>
            <label>
              <div>End</div>
              <input type="date" value={endDate} onChange={(event) => setEndDate(event.target.value || todayIso())} />
            </label>
            <label>
              <div>Yearly Rollup</div>
              <input
                type="number"
                value={selectedYear}
                onChange={(event) => setSelectedYear(Number(event.target.value || new Date().getUTCFullYear()))}
              />
            </label>
          </div>
          <Space>
            <Button type="primary" onClick={() => void loadCanonicalData()} loading={loading}>
              Refresh Canonical Analytics
            </Button>
            <Button onClick={() => void loadOpsData()} loading={opsLoading}>
              Refresh Operations
            </Button>
            <Button onClick={() => void loadTrendsData()} loading={trendsLoading}>
              Refresh Trends
            </Button>
            <Button onClick={() => void loadBreakdownData()} loading={breakdownsLoading}>
              Refresh Breakdowns
            </Button>
            <Button onClick={() => void loadCostBreakdown(selectedCostProcess)} loading={costLoading}>
              Refresh Cost Drilldown
            </Button>
            <Button onClick={() => void loadTranslationQualityCosts()} loading={qualityCostLoading}>
              Refresh Quality Projection
            </Button>
            <Button onClick={() => void loadLegacyDiagnostics()} loading={diagnosticsLoading}>
              Refresh Diagnostics
            </Button>
          </Space>
        </div>
      </Card>

      {loadNotice && (
        <Alert
          className="admin-dashboard-inline-alert"
          type="warning"
          showIcon
          message="Some analytics data could not be refreshed"
          description={loadNotice}
          action={
            <Button size="small" onClick={() => void loadCanonicalData()} loading={loading}>
              Retry overview
            </Button>
          }
        />
      )}

      <Card title="Overview" loading={loading} className="admin-dashboard-surface-card">
        <div className="admin-analytics-overview-band" style={sectionGridStyle}>
          <div className="admin-analytics-metric-row" style={processCardStyle}>
            <Text type="secondary">Window Provider USD</Text>
            <Title level={3}>{formatCurrency(overview?.window?.providerCostUsd)}</Title>
            <Text>{overview?.dateRange?.start} to {overview?.dateRange?.end}</Text>
          </div>
          <div className="admin-analytics-metric-row" style={processCardStyle}>
            <Text type="secondary">Window Charged Credits</Text>
            <Title level={3}>{formatCredits(overview?.window?.chargedCredits)}</Title>
            <Text>Credits charged in selected window</Text>
          </div>
          <div className="admin-analytics-metric-row" style={processCardStyle}>
            <Text type="secondary">Window Estimated Token USD</Text>
            <Title level={3}>{formatCurrency(overview?.window?.estimatedTokenCostUsd)}</Title>
            <Text>Derived from stage token pricing snapshots</Text>
          </div>
          <div className="admin-analytics-metric-row" style={processCardStyle}>
            <Text type="secondary">Translated Words</Text>
            <Title level={3}>{formatCompactNumber(overview?.window?.translatedWords)}</Title>
            <Text>Lifetime: {formatCompactNumber(overview?.lifetime?.translatedWords)}</Text>
          </div>
          <div className="admin-analytics-metric-row" style={processCardStyle}>
            <Text type="secondary">Proofread Words</Text>
            <Title level={3}>{formatCompactNumber(overview?.window?.proofreadWords)}</Title>
            <Text>Lifetime: {formatCompactNumber(overview?.lifetime?.proofreadWords)}</Text>
          </div>
          <div className="admin-analytics-metric-row" style={processCardStyle}>
            <Text type="secondary">Audio Hours</Text>
            <Title level={3}>{formatDurationHours(overview?.window?.audioSeconds)}</Title>
            <Text>Lifetime: {formatDurationHours(overview?.lifetime?.audioSeconds)}</Text>
          </div>
          <div className="admin-analytics-metric-row" style={processCardStyle}>
            <Text type="secondary">Cover Generation</Text>
            <Title level={3}>{formatCompactNumber(overview?.window?.coverGenerationCount)}</Title>
            <Text>Uploads: {formatCompactNumber(overview?.window?.coverUploadCount)}</Text>
          </div>
          <div className="admin-analytics-metric-row" style={processCardStyle}>
            <Text type="secondary">Books / Users</Text>
            <Title level={3}>
              {formatCompactNumber(overview?.window?.bookUploads)} / {formatCompactNumber(overview?.window?.userSignups)}
            </Title>
            <Text>Uploads and signups in selected window</Text>
          </div>
        </div>
          <div style={{ marginTop: 24 }}>
            <Title level={4}>Volume And Cost Trends</Title>
            {trendsLoading ? <Text type="secondary">Loading trends...</Text> : null}
            <div className="admin-analytics-chart-scroll">
            <ResponsiveContainer width="100%" height={280}>
            <LineChart data={trends?.items || []}>
              <CartesianGrid stroke={chartGridStroke} strokeDasharray="3 3" />
              <XAxis dataKey="date" tick={chartAxisStyle} axisLine={{ stroke: chartGridStroke }} tickLine={{ stroke: chartGridStroke }} />
              <YAxis tick={chartAxisStyle} axisLine={{ stroke: chartGridStroke }} tickLine={{ stroke: chartGridStroke }} />
              <Tooltip contentStyle={chartTooltipContentStyle} labelStyle={chartTooltipLabelStyle} />
              <Legend wrapperStyle={chartLegendWrapperStyle} />
              <Line type="monotone" dataKey="translatedWords" stroke={chartPalette.primary} name="Translated Words" />
              <Line type="monotone" dataKey="proofreadWords" stroke={chartPalette.success} name="Proofread Words" />
              <Line type="monotone" dataKey="providerCostUsd" stroke={chartPalette.danger} name="Provider USD" />
              <Line type="monotone" dataKey="estimatedTokenCostUsd" stroke={chartPalette.warning} name="Estimated Token USD" />
            </LineChart>
            </ResponsiveContainer>
            </div>
          </div>
      </Card>

      <Card
        title="Translation Quality Cost — Observe Comparison"
        loading={qualityCostLoading && !translationQualityCosts}
        className="admin-dashboard-surface-card"
      >
        {!qualityObserveAvailable ? (
          <Alert
            type="info"
            showIcon
            message="Waiting for observe-mode translation data"
            description="New translations will populate this comparison after the API and translation worker are deployed. Observe mode makes no extra model calls and does not alter translation output."
            style={{ marginBottom: 16 }}
          />
        ) : null}
        {Number(qualityObserveSummary?.missingPricingEventCount || 0) > 0 ? (
          <Alert
            type="warning"
            showIcon
            message="Some candidate pricing is missing"
            description={`${formatNumber(qualityObserveSummary?.missingPricingEventCount)} projection events were excluded from both sides of the savings comparison. Add input and output pricing to the assigned models for complete coverage.`}
            style={{ marginBottom: 16 }}
          />
        ) : null}

        <div style={sectionGridStyle}>
          <div style={processCardStyle}>
            <Text type="secondary">Current Comparable Cost</Text>
            <Title level={4}>{formatCurrency(qualityObserveSummary?.actualComparableCostUsd)}</Title>
            <Text>Observed calls with complete comparison pricing</Text>
          </div>
          <div style={processCardStyle}>
            <Text type="secondary">Projected Routed Cost</Text>
            <Title level={4}>{formatCurrency(qualityObserveSummary?.projectedRoutedCostUsd)}</Title>
            <Text>Same observed token counts on candidate models</Text>
          </div>
          <div style={processCardStyle}>
            <Text type="secondary">Projected Targeted Review</Text>
            <Title level={4}>{formatCurrency(qualityObserveSummary?.projectedTargetedReviewCostUsd)}</Title>
            <Text>{formatNumber(qualityObserveSummary?.reviewRequiredCount)} deterministic review flags</Text>
          </div>
          <div style={processCardStyle}>
            <Text type="secondary">Projected Total</Text>
            <Title level={4}>{formatCurrency(qualityObserveSummary?.projectedTotalCostUsd)}</Title>
            <Text>Candidate routing plus targeted AI review</Text>
          </div>
          <div style={processCardStyle}>
            <Text type="secondary">Projected Savings</Text>
            <Title
              level={4}
              style={{
                color: Number(qualityObserveSummary?.projectedSavingsUsd || 0) >= 0
                  ? token.colorSuccess
                  : token.colorError,
              }}
            >
              {formatCurrency(qualityObserveSummary?.projectedSavingsUsd)}
            </Title>
            <Text>{formatPercent(qualityObserveSummary?.projectedSavingsPercent)} of comparable actual cost</Text>
          </div>
          <div style={processCardStyle}>
            <Text type="secondary">Projection Pricing Coverage</Text>
            <Title level={4}>{formatPercent(qualityObserveSummary?.pricingCoverageRate)}</Title>
            <Text>
              {formatNumber(qualityObserveSummary?.pricedEventCount)} of{' '}
              {formatNumber(qualityObserveSummary?.projectionEventCount)} events
            </Text>
          </div>
          <div style={processCardStyle}>
            <Text type="secondary">Actual Quality Cost</Text>
            <Title level={4}>{formatCurrency(translationQualityCosts?.summary?.qualityCostUsd)}</Title>
            <Text>
              Comparable coverage: {formatPercent(qualityObserveSummary?.qualityCostCoverageRatio)}
            </Text>
          </div>
          <div style={processCardStyle}>
            <Text type="secondary">Additional Live Calls</Text>
            <Title level={4}>{formatNumber(qualityObserveSummary?.additionalModelCallsMade)}</Title>
            <Text>Observe estimates are computed without calling a model</Text>
          </div>
        </div>

        {qualityProjectedModels.length ? (
          <div style={{ marginTop: 16 }}>
            <Text type="secondary">Projected candidate models: </Text>
            {qualityProjectedModels.map((row) => (
              <Tag key={row.model}>
                {row.model}: {formatCurrency(row.projectedCostUsd)} / {formatNumber(row.eventCount)} events
              </Tag>
            ))}
          </div>
        ) : null}

        <div style={{ marginTop: 24 }}>
          <Title level={4}>Actual vs Projected Trend</Title>
          <div className="admin-analytics-chart-scroll">
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={qualityObserveTrendData}>
                <CartesianGrid stroke={chartGridStroke} strokeDasharray="3 3" />
                <XAxis dataKey="date" tick={chartAxisStyle} axisLine={{ stroke: chartGridStroke }} tickLine={{ stroke: chartGridStroke }} />
                <YAxis tick={chartAxisStyle} axisLine={{ stroke: chartGridStroke }} tickLine={{ stroke: chartGridStroke }} />
                <Tooltip contentStyle={chartTooltipContentStyle} labelStyle={chartTooltipLabelStyle} />
                <Legend wrapperStyle={chartLegendWrapperStyle} />
                <Line type="monotone" dataKey="actualComparableCostUsd" stroke={chartPalette.danger} name="Current Comparable USD" />
                <Line type="monotone" dataKey="projectedRoutedCostUsd" stroke={chartPalette.info} name="Projected Routed USD" />
                <Line type="monotone" dataKey="projectedReviewCostUsd" stroke={chartPalette.warning} name="Projected Review USD" />
                <Line type="monotone" dataKey="projectedTotalCostUsd" stroke={chartPalette.success} name="Projected Total USD" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div style={{ marginTop: 16 }}>
          <Table
            rowKey={(row: TranslationQualityCostRow) => row.stage}
            dataSource={translationQualityCosts?.items || []}
            columns={qualityCostColumns}
            pagination={{ pageSize: 10 }}
            scroll={{ x: 1400 }}
          />
        </div>
        <Text type="secondary">
          {qualityObserveSummary?.comparisonBasis || 'Projection-only comparison; no candidate model calls are made.'}
        </Text>
      </Card>

      <Card title="Costs" loading={costLoading && !currentCostBreakdown}>
        <div style={sectionGridStyle}>
          {processCostCards.map((item) => (
            <button
              key={item.key}
              type="button"
              onClick={() => setSelectedCostProcess(item.key)}
              style={{
                ...processCardStyle,
                textAlign: 'left',
                cursor: 'pointer',
                borderColor: selectedCostProcess === item.key ? token.colorPrimary : token.colorBorderSecondary,
              }}
            >
              <Text type="secondary">{item.label}</Text>
              <Title level={4}>{formatCurrency(item.summary?.providerCostUsd)}</Title>
              <div>Credits: {formatCredits(item.summary?.chargedCredits)}</div>
              <div>Estimated: {formatCurrency(item.summary?.estimatedTokenCostUsd)}</div>
              <div>Events: {formatNumber(item.summary?.eventCount)}</div>
            </button>
          ))}
        </div>

        <div style={{ marginTop: 24 }}>
          <Title level={4}>
            {PROCESS_LABELS[selectedCostProcess] || selectedCostProcess} Cost Drilldown
          </Title>
          <Text>
            Provider USD, charged credits, and estimated token cost stay separate. Unknown values render as —
            rather than being inferred.
          </Text>
          <div style={{ marginTop: 16 }}>
            <div className="admin-analytics-chart-scroll">
            <ResponsiveContainer width="100%" height={260}>
            <LineChart data={costTrendData}>
              <CartesianGrid stroke={chartGridStroke} strokeDasharray="3 3" />
              <XAxis dataKey="date" tick={chartAxisStyle} axisLine={{ stroke: chartGridStroke }} tickLine={{ stroke: chartGridStroke }} />
              <YAxis tick={chartAxisStyle} axisLine={{ stroke: chartGridStroke }} tickLine={{ stroke: chartGridStroke }} />
              <Tooltip contentStyle={chartTooltipContentStyle} labelStyle={chartTooltipLabelStyle} />
              <Legend wrapperStyle={chartLegendWrapperStyle} />
              <Line type="monotone" dataKey="providerCostUsd" stroke={chartPalette.danger} name="Provider USD" />
              <Line type="monotone" dataKey="chargedCredits" stroke={chartPalette.info} name="Charged Credits" />
              <Line type="monotone" dataKey="estimatedTokenCostUsd" stroke={chartPalette.warning} name="Estimated Token USD" />
            </LineChart>
            </ResponsiveContainer>
            </div>
          </div>
          <div style={{ marginTop: 16 }}>
            <Table
              rowKey={(row: BreakdownRow) => row.key}
              dataSource={stageDrilldownRows}
              columns={costColumns}
              pagination={{ pageSize: 8 }}
              scroll={tableScrollX}
            />
          </div>
        </div>
      </Card>

      <Card title="Breakdowns" loading={breakdownsLoading}>
        <div style={{ display: 'grid', gap: 24 }}>
          <div>
            <Title level={4}>Cost By Process</Title>
            <div className="admin-analytics-chart-scroll">
            <ResponsiveContainer width="100%" height={260}>
            <BarChart data={breakdowns.process}>
              <CartesianGrid stroke={chartGridStroke} strokeDasharray="3 3" />
              <XAxis dataKey="label" tick={chartAxisStyle} axisLine={{ stroke: chartGridStroke }} tickLine={{ stroke: chartGridStroke }} />
              <YAxis tick={chartAxisStyle} axisLine={{ stroke: chartGridStroke }} tickLine={{ stroke: chartGridStroke }} />
              <Tooltip contentStyle={chartTooltipContentStyle} labelStyle={chartTooltipLabelStyle} />
              <Legend wrapperStyle={chartLegendWrapperStyle} />
              <Bar dataKey="providerCostUsd" fill={chartPalette.danger} name="Provider USD" />
              <Bar dataKey="estimatedTokenCostUsd" fill={chartPalette.warning} name="Estimated Token USD" />
            </BarChart>
            </ResponsiveContainer>
            </div>
            <Table
              rowKey={(row: BreakdownRow) => row.key}
              dataSource={breakdowns.process}
              columns={breakdownColumns}
              pagination={{ pageSize: 6 }}
              scroll={tableScrollX}
            />
          </div>

          <div>
            <Title level={4}>Providers</Title>
            <Table
              rowKey={(row: BreakdownRow) => row.key}
              dataSource={breakdowns.provider}
              columns={breakdownColumns}
              pagination={{ pageSize: 6 }}
              scroll={tableScrollX}
            />
          </div>

          <div>
            <Title level={4}>Models</Title>
            <Table
              rowKey={(row: BreakdownRow) => row.key}
              dataSource={breakdowns.model}
              columns={breakdownColumns}
              pagination={{ pageSize: 6 }}
              scroll={tableScrollX}
            />
          </div>

          <div>
            <Title level={4}>Language Pairs</Title>
            <Table
              rowKey={(row: BreakdownRow) => row.key}
              dataSource={breakdowns.language_pair}
              columns={breakdownColumns}
              pagination={{ pageSize: 6 }}
              scroll={tableScrollX}
            />
          </div>
        </div>
      </Card>

      <Card title="Operations" loading={opsLoading}>
        <div style={{ display: 'grid', gap: 24 }}>
          <div style={inlineFieldGridStyle}>
            <label>
              <div>Year</div>
              <input
                type="number"
                value={opsForm.year}
                onChange={(event) => setOpsForm((current) => ({ ...current, year: event.target.value }))}
              />
            </label>
            <label>
              <div>Start Date</div>
              <input
                type="date"
                value={opsForm.start_date}
                onChange={(event) => setOpsForm((current) => ({ ...current, start_date: event.target.value }))}
              />
            </label>
            <label>
              <div>End Date</div>
              <input
                type="date"
                value={opsForm.end_date}
                onChange={(event) => setOpsForm((current) => ({ ...current, end_date: event.target.value }))}
              />
            </label>
            <label>
              <div>User ID</div>
              <input
                type="text"
                value={opsForm.user_id}
                onChange={(event) => setOpsForm((current) => ({ ...current, user_id: event.target.value }))}
              />
            </label>
            <label>
              <div>Book ID</div>
              <input
                type="text"
                value={opsForm.book_id}
                onChange={(event) => setOpsForm((current) => ({ ...current, book_id: event.target.value }))}
              />
            </label>
            <label>
              <div>Limit</div>
              <input
                type="number"
                value={opsForm.limit}
                onChange={(event) => setOpsForm((current) => ({ ...current, limit: event.target.value }))}
              />
            </label>
          </div>

          <div style={inlineFieldGridStyle}>
            <label>
              <input
                type="checkbox"
                checked={opsForm.dry_run}
                onChange={(event) => setOpsForm((current) => ({ ...current, dry_run: event.target.checked }))}
              />{' '}
              Dry run
            </label>
            <label>
              <input
                type="checkbox"
                checked={opsForm.allow_global}
                onChange={(event) => setOpsForm((current) => ({ ...current, allow_global: event.target.checked }))}
              />{' '}
              Allow global
            </label>
            <label>
              <input
                type="checkbox"
                checked={opsForm.include_stage}
                onChange={(event) => setOpsForm((current) => ({ ...current, include_stage: event.target.checked }))}
              />{' '}
              Include stage events
            </label>
            <label>
              <input
                type="checkbox"
                checked={opsForm.include_generation}
                onChange={(event) => setOpsForm((current) => ({ ...current, include_generation: event.target.checked }))}
              />{' '}
              Include generation events
            </label>
            <label>
              <input
                type="checkbox"
                checked={opsForm.include_business}
                onChange={(event) => setOpsForm((current) => ({ ...current, include_business: event.target.checked }))}
              />{' '}
              Include business events
            </label>
            <label>
              <input
                type="checkbox"
                checked={opsForm.rebuild_rollups}
                onChange={(event) => setOpsForm((current) => ({ ...current, rebuild_rollups: event.target.checked }))}
              />{' '}
              Rebuild rollups after backfill
            </label>
          </div>

          <Space>
            <Button type="primary" loading={opsSubmitting} onClick={() => void submitBackfill()}>
              Run Canonical Backfill
            </Button>
            <Button loading={opsSubmitting} onClick={() => void submitRollupRebuild()}>
              Rebuild Canonical Rollups
            </Button>
          </Space>

          <div>
            <Title level={4}>Schedules</Title>
            <div style={sectionGridStyle}>
              {schedules.map((schedule) => (
                <div key={schedule.scheduleKey} style={processCardStyle}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                    <strong>{schedule.label}</strong>
                    <Tag>{schedule.lastStatus || 'idle'}</Tag>
                  </div>
                  <div>{schedule.cadenceLabel}</div>
                  <div>Next: {formatIsoShort(schedule.nextRunAt)}</div>
                  <div>Last: {formatIsoShort(schedule.lastRunAt)}</div>
                  <div>Last Job: {schedule.lastJobId || '—'}</div>
                </div>
              ))}
            </div>
          </div>

          <div>
            <Title level={4}>Recent Jobs</Title>
            <Table
              rowKey={(row: AnalyticsAdminJob) => row.job_id}
              dataSource={jobs}
              columns={jobsColumns}
              pagination={{ pageSize: 6 }}
              scroll={tableScrollX}
            />
          </div>
        </div>
      </Card>

      <Card title={`Year ${selectedYear}`} loading={yearlyLoading}>
        <div style={sectionGridStyle}>
          <div style={processCardStyle}>
            <Text type="secondary">Translated Words</Text>
            <Title level={4}>{formatCompactNumber(yearly?.totals?.translatedWords)}</Title>
            <Text>Users with stats: {formatNumber(yearly?.totals?.userCount)}</Text>
          </div>
          <div style={processCardStyle}>
            <Text type="secondary">Proofread Words</Text>
            <Title level={4}>{formatCompactNumber(yearly?.totals?.proofreadWords)}</Title>
            <Text>Translated books: {formatNumber(yearly?.totals?.translatedBooks)}</Text>
          </div>
          <div style={processCardStyle}>
            <Text type="secondary">Audio</Text>
            <Title level={4}>{formatDurationHours(yearly?.totals?.audioSeconds)}</Title>
            <Text>Items: {formatNumber(yearly?.totals?.audioItemCount)}</Text>
          </div>
          <div style={processCardStyle}>
            <Text type="secondary">Covers</Text>
            <Title level={4}>{formatNumber(yearly?.totals?.coverGenerationCount)}</Title>
            <Text>Uploads: {formatNumber(yearly?.totals?.coverUploadCount)}</Text>
          </div>
        </div>
        <div style={{ marginTop: 16 }}>
          <Title level={4}>Top Languages</Title>
          <Table
            rowKey={(row: any) => row.language}
            dataSource={yearly?.topLanguages || []}
            columns={[
              { title: 'Language', dataIndex: 'language', key: 'language' },
              { title: 'Words', dataIndex: 'value', key: 'value', render: (value: number) => formatNumber(value) },
            ]}
            pagination={false}
          />
        </div>
      </Card>

      <Card title="Legacy Diagnostics" loading={diagnosticsLoading}>
        <div style={{ display: 'grid', gap: 24 }}>
          <Space>
            <Button loading={legacyRebuildLoading} onClick={() => void rebuildLegacyUsageAggregates()}>
              Rebuild Legacy Usage Aggregates
            </Button>
            <Button onClick={() => void exportUsageCountersToExcel(API_BASE)}>Export Counters</Button>
          </Space>

          <div style={sectionGridStyle}>
            <div style={processCardStyle}>
              <Text type="secondary">Legacy Token Total</Text>
              <Title level={4}>{formatCompactNumber(legacyUsage?.totals?.total)}</Title>
              <Text>Input: {formatCompactNumber(legacyUsage?.totals?.input)}</Text>
            </div>
            <div style={processCardStyle}>
              <Text type="secondary">Legacy Cached Tokens</Text>
              <Title level={4}>{formatCompactNumber(legacyUsage?.totals?.cached)}</Title>
              <Text>Reasoning: {formatCompactNumber(legacyUsage?.totals?.reasoning)}</Text>
            </div>
            <div style={processCardStyle}>
              <Text type="secondary">Legacy Model Rows</Text>
              <Title level={4}>{formatNumber(legacyModelBreakdown?.models?.length)}</Title>
              <Text>Stats counters: {formatNumber(Object.keys(legacyCounters || {}).length)}</Text>
            </div>
          </div>

          <div>
            <Title level={4}>Window Process Summary</Title>
            <Table
              rowKey={(row: BreakdownRow) => row.key}
              dataSource={windowProcessRows}
              columns={breakdownColumns}
              pagination={false}
              scroll={tableScrollX}
            />
          </div>
        </div>
      </Card>
    </div>
  )
}

export default AnalyticsTab
